import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Tabs, Tab, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  LinearProgress, Divider, Alert
} from '@mui/material';
import {
  Psychology, Build, BugReport, PlayArrow, Refresh, CheckCircle,
  TrendingUp, SmartToy, Code, Rule, Assignment, AutoAwesome, Terminal, Settings,
  Shield, MenuBook
} from '@mui/icons-material';
import { agentCenter } from '../services/api';
import toast from 'react-hot-toast';

export default function AgentCenterPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [traderData, setTraderData] = useState(null);
  const [realityData, setRealityData] = useState(null);
  const [thesesData, setThesesData] = useState([]);
  const [devData, setDevData] = useState(null);
  const [testerData, setTesterData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [devRequests, setDevRequests] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [testResults, setTestResults] = useState(null);
  const [learning, setLearning] = useState(null);
  const [activity, setActivity] = useState(null);

  const safeStr = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const res = await agentCenter.overview();
      setOverviewData(res.data?.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTabContent = async (tabIndex) => {
    setLoading(true);
    try {
      if (tabIndex === 0) await fetchOverview();
      else if (tabIndex === 1) {
        const res = await agentCenter.trader();
        setTraderData(res.data?.data || null);
      } else if (tabIndex === 2) {
        const res = await agentCenter.realityChecks();
        setRealityData(res.data?.data || null);
      } else if (tabIndex === 3) {
        const res = await agentCenter.theses();
        setThesesData(res.data?.data || []);
      } else if (tabIndex === 4) {
        const res = await agentCenter.dev();
        setDevData(res.data?.data || null);
      } else if (tabIndex === 5) {
        const res = await agentCenter.tester();
        setTesterData(res.data?.data || null);
      } else if (tabIndex === 6) {
        const res = await agentCenter.tasks();
        setTasks(res.data?.data || []);
      } else if (tabIndex === 7) {
        const res = await agentCenter.decisions();
        setDecisions(res.data?.data || []);
      } else if (tabIndex === 8) {
        const res = await agentCenter.devRequests();
        setDevRequests(res.data?.data || []);
      } else if (tabIndex === 9) {
        const res = await agentCenter.bugs();
        setBugs(res.data?.data || []);
      } else if (tabIndex === 10) {
        const res = await agentCenter.testResults();
        setTestResults(res.data?.data || null);
      } else if (tabIndex === 11) {
        const res = await agentCenter.learning();
        setLearning(res.data?.data || null);
      } else if (tabIndex === 12) {
        const res = await agentCenter.activity();
        setActivity(res.data?.data || null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTabContent(activeTab);
  }, [activeTab]);

  const handleTriggerCycle = async () => {
    try {
      setLoading(true);
      toast.loading('Executing Multi-Agent Intelligence Council Cycle...', { id: 'cycle' });
      await agentCenter.triggerCycle();
      toast.success('Multi-Agent council cycle executed successfully!', { id: 'cycle' });
      fetchTabContent(activeTab);
    } catch (err) {
      toast.error('Cycle failed: ' + err.message, { id: 'cycle' });
    } finally {
      setLoading(false);
    }
  };

  const handleDevAction = async (id, action) => {
    try {
      await agentCenter.devRequestAction(id, action);
      toast.success(`Request ${action}d successfully!`);
      fetchTabContent(8);
    } catch (err) {
      toast.error('Action failed: ' + err.message);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Top Banner */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #0a1929 0%, #1e3a8a 100%)', color: '#fff', borderRadius: 3 }}>
        <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SmartToy sx={{ fontSize: 42, color: '#60a5fa' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                AI AGENT CENTER
              </Typography>
              <Typography variant="body2" sx={{ color: '#93c5fd' }}>
                Superadmin Orchestration Hub • Senior Trader • Reality Checker • Investment Researcher • Risk Analyst • Developer • Tester
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Refresh />}
              onClick={() => fetchTabContent(activeTab)}
              sx={{ borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff' } }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PlayArrow />}
              onClick={handleTriggerCycle}
              disabled={loading}
              sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, fontWeight: 700 }}
            >
              Run Agent Cycle Now
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* 14 Tabs - Multi-Agent Committee */}
      <Paper sx={{ mb: 3, borderRadius: 2, bgcolor: '#fff' }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab icon={<AutoAwesome />} iconPosition="start" label="Overview" />
          <Tab icon={<Psychology />} iconPosition="start" label="Senior Trader" />
          <Tab icon={<Shield />} iconPosition="start" label="Reality Checker" />
          <Tab icon={<MenuBook />} iconPosition="start" label="Investment Research" />
          <Tab icon={<Build />} iconPosition="start" label="Developer" />
          <Tab icon={<Rule />} iconPosition="start" label="Tester" />
          <Tab icon={<Assignment />} iconPosition="start" label="Tasks" />
          <Tab icon={<TrendingUp />} iconPosition="start" label="Trade Decisions" />
          <Tab icon={<Code />} iconPosition="start" label="Dev Requests" />
          <Tab icon={<BugReport />} iconPosition="start" label="Bugs" />
          <Tab icon={<CheckCircle />} iconPosition="start" label="Test Results" />
          <Tab icon={<Psychology />} iconPosition="start" label="Learning" />
          <Tab icon={<Terminal />} iconPosition="start" label="Agent Activity" />
          <Tab icon={<Settings />} iconPosition="start" label="Settings" />
        </Tabs>
      </Paper>

      {/* Tab 0: Overview */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderTop: '4px solid #2563eb', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Psychology sx={{ color: '#2563eb' }} /> Senior Trader
                  </Typography>
                  <Chip label="ONLINE" color="success" size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Market Strategy & Trade Committee Lead</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption">Decisions Today:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{overviewData?.metrics?.decisionsToday || 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderTop: '4px solid #ef4444', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Shield sx={{ color: '#ef4444' }} /> Reality Checker
                  </Typography>
                  <Chip label="ONLINE" color="success" size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Adversarial Skeptic & Trap Veto Gate</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption">Checks / Vetoes Today:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef4444' }}>
                    {overviewData?.metrics?.realityChecksToday || 0} checks / {overviewData?.metrics?.realityVetoesToday || 0} vetoed
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderTop: '4px solid #10b981', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MenuBook sx={{ color: '#10b981' }} /> Investment Researcher
                  </Typography>
                  <Chip label="ONLINE" color="success" size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Dual Bull vs Bear & Thesis Breakers</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption">Theses Today:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981' }}>{overviewData?.metrics?.researchThesesToday || 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderTop: '4px solid #7c3aed', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build sx={{ color: '#7c3aed' }} /> Developer Agent
                  </Typography>
                  <Chip label="ONLINE" color="success" size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Implementation & Self-Healing Engine</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption">Active Dev Requests:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{overviewData?.metrics?.openDevRequests || 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderTop: '4px solid #059669', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rule sx={{ color: '#059669' }} /> Tester Agent
                  </Typography>
                  <Chip label="ONLINE" color="success" size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>12-Domain Automated QA Assertion Engine</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption">QA Pass Rate:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669' }}>{overviewData?.metrics?.qaPassRate || 100}%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Real-Time Agent Activity Telemetry</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Agent</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overviewData?.recentLogs?.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{new Date(log.created_at).toLocaleTimeString()}</TableCell>
                        <TableCell><Chip label={log.agent_type} size="small" color="primary" /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{log.action}</TableCell>
                        <TableCell><Typography variant="caption">{safeStr(log.details)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Senior Trader */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Senior Trader Agent Live Market Decisions & Technical Analysis
            </Typography>
            <Chip label="LIVE TELEMETRY" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 800 }} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>SYMBOL</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>INSTRUMENT</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ACTION</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ENTRY / SL / TARGET</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>CONFIDENCE</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>OUTCOME</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>TECHNICAL REASONING</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!traderData?.decisions || traderData.decisions.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No decisions generated yet. Click "Run Agent Cycle Now" to trigger market analysis.
                    </TableCell>
                  </TableRow>
                ) : (
                  traderData.decisions.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{d.symbol}</TableCell>
                      <TableCell>
                        <Chip label={`${d.instrument_type}${d.option_type !== 'NONE' ? ' (' + d.option_type + ')' : ''}`} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={d.action} size="small" sx={{ fontWeight: 800, bgcolor: d.action === 'BUY' ? '#dcfce7' : '#fee2e2', color: d.action === 'BUY' ? '#15803d' : '#991b1b' }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.825rem' }}>
                        ₹{parseFloat(d.entry_price || 0).toFixed(1)} / ₹{parseFloat(d.stop_loss || 0).toFixed(1)} / ₹{parseFloat(d.target_price || 0).toFixed(1)}
                      </TableCell>
                      <TableCell>
                        <Chip label={`${parseFloat(d.confidence_score || 0).toFixed(0)}%`} size="small" sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#2563eb' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={d.outcome || 'PASSED_TO_RISK'} size="small" sx={{ fontWeight: 700, bgcolor: '#ecfdf5', color: '#059669' }} />
                      </TableCell>
                      <TableCell sx={{ color: '#475569', fontSize: '0.8rem', maxWidth: 320 }}>{d.reason}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 2: Reality Checker */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Shield sx={{ color: '#ef4444' }} /> Reality Checker — Adversarial Trade Verification & Veto Gate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inspects all candidate signals for bull/bear traps, R:R failure, volume exhaustion, and options OI barriers.
              </Typography>
            </Box>
            <Chip label="SKEPTIC RED-TEAM ACTIVE" size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 800 }} />
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" color="text.secondary">Total Audits Run</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{realityData?.summary?.total || 0}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#ecfdf5', borderRadius: 2, border: '1px solid #a7f3d0' }}>
                <Typography variant="caption" color="#047857">Approved Trades</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669' }}>{realityData?.summary?.approved || 0}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
                <Typography variant="caption" color="#b91c1c">Vetoed (Capital Protected)</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#ef4444' }}>{realityData?.summary?.vetoed || 0}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                <Typography variant="caption" color="#1d4ed8">Avg Trap Score</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2563eb' }}>
                  {realityData?.summary?.avg_trap_score ? parseFloat(realityData.summary.avg_trap_score).toFixed(1) : 0}/100
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>TIMESTAMP</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SYMBOL & ACTION</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>VERDICT</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>TRAP SCORE</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>RISK : REWARD</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>AUDIT DETAILS / REASONS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>THESIS BREAKER TRIGGER</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!realityData?.checks || realityData.checks.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No reality checks recorded yet. Run a market cycle to trigger reality check audits.
                    </TableCell>
                  </TableRow>
                ) : (
                  realityData.checks.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(c.created_at).toLocaleTimeString()}</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{c.symbol} ({c.action})</TableCell>
                      <TableCell>
                        <Chip
                          label={c.verdict}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            bgcolor: c.verdict === 'APPROVED' ? '#dcfce7' : '#fee2e2',
                            color: c.verdict === 'APPROVED' ? '#15803d' : '#b91c1c'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: c.trap_score > 30 ? '#ef4444' : '#10b981' }}>
                          {c.trap_score}/100
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{c.risk_reward_ratio ? `${c.risk_reward_ratio}:1` : 'N/A'}</TableCell>
                      <TableCell sx={{ color: '#475569', fontSize: '0.8rem', maxWidth: 350 }}>
                        {c.verdict === 'VETOED'
                          ? <strong style={{ color: '#dc2626' }}>{safeStr(c.rejection_reasons)}</strong>
                          : c.recommendation || 'All verification gates passed'}
                      </TableCell>
                      <TableCell sx={{ color: '#0f172a', fontSize: '0.8rem', fontStyle: 'italic', maxWidth: 300 }}>
                        {c.thesis_breaker || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 3: Investment Research */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuBook sx={{ color: '#10b981' }} /> Investment Researcher — Dual Bull vs Bear Theses
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Institutional variant perception dossiers with quantified downside scenarios and falsifiable thesis breakers.
              </Typography>
            </Box>
            <Chip label="VARIANT PERCEPTION" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 800 }} />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>TIMESTAMP</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SYMBOL & DIRECTION</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>VARIANT EDGE</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>BULL CASE CATALYSTS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>BEAR CASE RISKS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>THESIS BREAKER (INVALIDATION)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!thesesData || thesesData.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No research theses formulated yet. Run an agent cycle to initiate research.
                    </TableCell>
                  </TableRow>
                ) : (
                  thesesData.map((th) => {
                    const bullObj = typeof th.bull_case === 'string' ? JSON.parse(th.bull_case || '{}') : (th.bull_case || {});
                    const bearObj = typeof th.bear_case === 'string' ? JSON.parse(th.bear_case || '{}') : (th.bear_case || {});
                    return (
                      <TableRow key={th.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(th.created_at).toLocaleTimeString()}</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>
                          {th.symbol}
                          <Chip
                            label={th.direction}
                            size="small"
                            sx={{
                              ml: 1,
                              fontWeight: 700,
                              bgcolor: th.direction === 'BULLISH' ? '#dcfce7' : '#fee2e2',
                              color: th.direction === 'BULLISH' ? '#15803d' : '#b91c1c'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#2563eb' }}>{th.variant_perception_score || 50}%</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 300, color: '#15803d' }}>
                          {bullObj.drivers ? bullObj.drivers.join(' | ') : safeStr(bullObj)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 300, color: '#b91c1c' }}>
                          {bearObj.risks ? bearObj.risks.join(' | ') : safeStr(bearObj)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 280, fontStyle: 'italic', fontWeight: 600, color: '#0f172a' }}>
                          {th.thesis_breaker}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 4: Developer */}
      {activeTab === 4 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Developer Agent Task Queue</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Task Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devData?.devTasks?.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{t.task_code}</TableCell>
                    <TableCell>{t.title}</TableCell>
                    <TableCell><Chip label={t.status} size="small" color="info" /></TableCell>
                    <TableCell><Chip label={t.priority} size="small" color="warning" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 5: Tester */}
      {activeTab === 5 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Tester Agent QA Runs & Bugs</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Run Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Domain</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Passed / Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testerData?.testRuns?.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{r.run_code}</TableCell>
                    <TableCell>{r.domain}</TableCell>
                    <TableCell>{r.passed} / {r.total_tests}</TableCell>
                    <TableCell><Chip label={r.status} size="small" color={r.status === 'PASSED' ? 'success' : 'error'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 6: Tasks */}
      {activeTab === 6 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>All Inter-Agent Tasks</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Task Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Agent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{t.task_code}</TableCell>
                    <TableCell>{t.title}</TableCell>
                    <TableCell><Chip label={t.agent_type} size="small" /></TableCell>
                    <TableCell><Chip label={t.status} size="small" color="primary" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 7: Trade Decisions */}
      {activeTab === 7 && (
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2.5 }}>
            Complete AI Decision Audit Log ({decisions.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>DECISION CODE</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SYMBOL</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>TYPE</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ACTION</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ENTRY / SL / TARGET</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>CONFIDENCE</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {decisions.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#64748b', fontSize: '0.8rem' }}>
                      {d.decision_code}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{d.symbol}</TableCell>
                    <TableCell>
                      <Chip label={`${d.instrument_type}${d.option_type !== 'NONE' ? ' ' + d.option_type : ''}`} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={d.action} size="small" sx={{ fontWeight: 800, bgcolor: d.action === 'BUY' ? '#dcfce7' : '#fee2e2', color: d.action === 'BUY' ? '#15803d' : '#991b1b' }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.825rem' }}>
                      ₹{parseFloat(d.entry_price || 0).toFixed(1)} / ₹{parseFloat(d.stop_loss || 0).toFixed(1)} / ₹{parseFloat(d.target_price || 0).toFixed(1)}
                    </TableCell>
                    <TableCell>
                      <Chip label={`${parseFloat(d.confidence_score || 0).toFixed(0)}%`} size="small" sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#2563eb' }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={d.outcome || 'PASSED_TO_RISK'} size="small" sx={{ fontWeight: 700, bgcolor: '#ecfdf5', color: '#059669' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 8: Dev Requests */}
      {activeTab === 8 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Development Requests</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devRequests.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{r.request_code}</TableCell>
                    <TableCell>{r.title}</TableCell>
                    <TableCell><Chip label={r.module} size="small" /></TableCell>
                    <TableCell><Chip label={r.status} size="small" color="info" /></TableCell>
                    <TableCell>
                      {r.status === 'NEW' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="contained" color="success" onClick={() => handleDevAction(r.id, 'approve')}>Approve</Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => handleDevAction(r.id, 'reject')}>Reject</Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 9: Bugs */}
      {activeTab === 9 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Bug Reports</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Bug Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Severity</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bugs.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{b.bug_code}</TableCell>
                    <TableCell>{b.title}</TableCell>
                    <TableCell><Chip label={b.severity} size="small" color="error" /></TableCell>
                    <TableCell><Chip label={b.status} size="small" variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 10: Test Results */}
      {activeTab === 10 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>QA Test Results & Assertions</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Test Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Domain</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testResults?.results?.map((res) => (
                  <TableRow key={res.id}>
                    <TableCell>{res.test_name}</TableCell>
                    <TableCell><Chip label={res.domain} size="small" /></TableCell>
                    <TableCell><Chip label={res.status} size="small" color={res.status === 'PASS' ? 'success' : 'error'} /></TableCell>
                    <TableCell>{res.duration_ms} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 11: Learning */}
      {activeTab === 11 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Strategy Learning Knowledge Base ({learning?.learningRecords?.length || 0})
              </Typography>
              <Button size="small" variant="outlined" startIcon={<PlayArrow />} onClick={handleTriggerCycle}>
                Run Agent Cycle
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Lesson Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Strategy</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Win Rate</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Observation</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Suggested Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Conf. Mod</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!learning?.learningRecords || learning.learningRecords.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#64748b' }}>
                        No learning records found yet. Click "Run Agent Cycle" to trigger automated strategy review.
                      </TableCell>
                    </TableRow>
                  ) : (
                    learning.learningRecords.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{l.lesson_code}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{l.symbol || 'NIFTY'}</TableCell>
                        <TableCell><Chip label={l.strategy} size="small" sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 700 }} /></TableCell>
                        <TableCell sx={{ fontWeight: 700, color: (l.win_rate || 0) >= 50 ? '#10b981' : '#ef4444' }}>
                          {parseFloat(l.win_rate || 0).toFixed(1)}%
                        </TableCell>
                        <TableCell sx={{ color: '#475569' }}>{l.observation}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2563eb' }}>{l.suggested_action}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{parseFloat(l.confidence_modifier || 1.0).toFixed(2)}x</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
              Post-Trade Reviews & Lessons Learned ({learning?.tradeReviews?.length || 0})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Review Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Result</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>P&L</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mistake Identified</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Lesson Learned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!learning?.tradeReviews || learning.tradeReviews.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748b' }}>
                        No post-trade reviews recorded yet. Reviews are generated automatically as trades close.
                      </TableCell>
                    </TableRow>
                  ) : (
                    learning.tradeReviews.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{r.trade_code}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.symbol}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.result}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: r.result === 'WIN' ? '#dcfce7' : '#fee2e2',
                              color: r.result === 'WIN' ? '#15803d' : '#991b1b',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: (r.final_pnl || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                          ₹{parseFloat(r.final_pnl || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ color: '#ef4444', fontSize: '0.85rem' }}>{r.mistakes || 'None'}</TableCell>
                        <TableCell sx={{ color: '#0f172a', fontWeight: 500, fontSize: '0.85rem' }}>{r.lessons || 'N/A'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Tab 12: Agent Activity */}
      {activeTab === 12 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Inter-Agent Messages & Activity Log</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sender → Receiver</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Content</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activity?.messages?.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{new Date(m.created_at).toLocaleTimeString()}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{m.sender_agent} → {m.receiver_agent}</TableCell>
                    <TableCell><Chip label={m.message_type} size="small" color="secondary" /></TableCell>
                    <TableCell><Typography variant="body2">{safeStr(m.content)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 13: Settings */}
      {activeTab === 13 && (
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>AI Agent Center Controls & Configuration</Typography>
          <Alert severity="success" sx={{ mb: 3 }}>
            <strong>All AI Agents (Senior Trader, Reality Checker, Investment Researcher, Risk Analyst, Developer, Tester) are ONLINE.</strong> Background orchestrator loop is running on an active synchronized schedule.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleTriggerCycle} startIcon={<PlayArrow />}>
              Run Manual Agent Cycle
            </Button>
            <Button variant="outlined" color="inherit" onClick={() => fetchTabContent(activeTab)} startIcon={<Refresh />}>
              Reload Telemetry
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
